'use client'

import { useState } from 'react'
import { Search, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { faqData, FAQItem } from '@/data/faq'
import { searchFAQs } from '@/data/faq'

export default function HelpPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<FAQItem[]>([])

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    if (query.trim()) {
      setSearchResults(searchFAQs(query))
    } else {
      setSearchResults([])
    }
  }

  const toggleItem = (index: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedItems(newExpanded)
  }

  const displayData = searchQuery.trim() && searchResults.length > 0
    ? [{ id: 'search-results', name: 'Search Results', description: '', items: searchResults }]
    : selectedCategory
      ? faqData.filter(cat => cat.id === selectedCategory)
      : faqData

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <HelpCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-4xl font-display font-bold text-stone-900 mb-3">
              Help Center
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Find answers to common questions about using LandLordNoAgent
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        {!searchQuery.trim() && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === null
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-stone-700 hover:bg-stone-50 border border-stone-200'
                }`}
              >
                All Categories
              </button>
              {faqData.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-stone-700 hover:bg-stone-50 border border-stone-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* FAQ Sections */}
        {displayData.length > 0 ? (
          <div className="space-y-8">
            {displayData.map((category) => (
              <div key={category.id} className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
                <div className="bg-stone-50 px-6 py-4 border-b border-stone-200">
                  <h2 className="text-xl font-display font-bold text-stone-900">{category.name}</h2>
                  {category.description && (
                    <p className="text-sm text-stone-600 mt-1">{category.description}</p>
                  )}
                </div>

                <div className="divide-y divide-stone-200">
                  {category.items.map((item, index) => {
                    const itemId = `${category.id}-${index}`
                    const isExpanded = expandedItems.has(itemId)

                    return (
                      <div key={itemId} className="p-6">
                        <button
                          onClick={() => toggleItem(itemId)}
                          className="w-full flex items-start justify-between text-left group"
                        >
                          <h3 className="text-lg font-semibold text-stone-900 group-hover:text-emerald-600 transition-colors pr-4">
                            {item.question}
                          </h3>
                          <div className="flex-shrink-0">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-stone-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-stone-400" />
                            )}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="mt-4 pl-0">
                            <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                              {item.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
            <HelpCircle className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No results found</h3>
            <p className="text-stone-600">
              Try searching with different keywords or browse by category.
            </p>
          </div>
        )}

        {/* Still Need Help Section */}
        <div className="mt-12 bg-emerald-50 rounded-2xl border border-emerald-200 p-8 text-center">
          <h3 className="text-xl font-semibold text-stone-900 mb-2">Still need help?</h3>
          <p className="text-stone-600 mb-4">
            Can't find what you're looking for? Contact our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@landlordnoagent.app"
              className="inline-flex items-center justify-center px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
            >
              Email Support
            </a>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-stone-700 rounded-xl font-semibold border border-stone-300 hover:bg-stone-50 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

